import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { AlertTriangle, ChevronLeft, Loader2, MessageCircle, Send } from "lucide-react";

type Message = {
  id: string;
  user_id: string;
  sender_name: string;
  content: string;
  created_at: string;
};

function cx(...classes: Array<string | false | undefined | null>) {
  return classes.filter(Boolean).join(" ");
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

function formatDay(iso: string) {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Aujourd'hui";
  if (d.toDateString() === yesterday.toDateString()) return "Hier";
  return d.toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long" });
}

function groupByDay(messages: Message[]) {
  const groups: { day: string; messages: Message[] }[] = [];
  for (const msg of messages) {
    const day = new Date(msg.created_at).toDateString();
    const last = groups[groups.length - 1];
    if (last && last.day === day) {
      last.messages.push(msg);
    } else {
      groups.push({ day, messages: [msg] });
    }
  }
  return groups;
}

export default function TeamChat() {
  const { teamId, clubId } = useParams<{ teamId: string; clubId?: string }>();
  const navigate = useNavigate();
  const goBack = useCallback(() => {
    if (clubId) {
      navigate(`/club/${clubId}/team/${teamId}`);
    } else {
      navigate(-1);
    }
  }, [clubId, teamId, navigate]);

  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [senderName, setSenderName] = useState("");

  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (!teamId) return;
    let cancelled = false;

    async function init() {
      try {
        setLoading(true);
        setError("");

        const {
          data: { user },
          error: authErr,
        } = await supabase.auth.getUser();
        if (authErr) throw authErr;
        if (!user) throw new Error("Utilisateur non connecté.");

        setCurrentUserId(user.id);

        const { data: profile } = await supabase
          .from("user_profiles")
          .select("full_name, pseudo, public_use_pseudo")
          .eq("id", user.id)
          .maybeSingle();

        const name =
          profile?.public_use_pseudo && profile?.pseudo
            ? profile.pseudo
            : profile?.full_name?.trim() || user.email?.split("@")[0] || "Membre";

        if (!cancelled) setSenderName(name);

        const { data, error: msgErr } = await supabase
          .from("team_messages")
          .select("id, user_id, sender_name, content, created_at")
          .eq("team_id", teamId)
          .order("created_at", { ascending: true })
          .limit(200);

        if (msgErr) throw msgErr;
        if (!cancelled) setMessages((data || []) as Message[]);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || "Impossible de charger les messages.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    init();

    const channel = supabase
      .channel(`team-chat-${teamId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "team_messages",
          filter: `team_id=eq.${teamId}`,
        },
        (payload) => {
          if (!cancelled) {
            setMessages((prev) => {
              if (prev.some((m) => m.id === payload.new.id)) return prev;
              return [...prev, payload.new as Message];
            });
          }
        }
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [teamId]);

  async function sendMessage() {
    const text = input.trim();
    if (!text || sending || !teamId || !currentUserId) return;

    setSending(true);
    const { data: inserted, error: sendErr } = await supabase
      .from("team_messages")
      .insert({
        team_id: teamId,
        user_id: currentUserId,
        sender_name: senderName,
        content: text,
      })
      .select("id, user_id, sender_name, content, created_at")
      .single();
    setSending(false);

    if (sendErr) {
      setError(sendErr.message);
    } else {
      setMessages((prev) => {
        if (prev.some((m) => m.id === inserted.id)) return prev;
        return [...prev, inserted as Message];
      });
      setInput("");
      inputRef.current?.focus();
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  const groups = groupByDay(messages);

  return (
    <div className="chat-viewport flex flex-col">
      <div className="flex items-center gap-2 mb-4 shrink-0">
        <button
          type="button"
          onClick={goBack}
          aria-label="Retour"
          title="Retour"
          className="inline-flex items-center justify-center h-9 w-9 shrink-0 rounded-xl border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <MessageCircle className="h-5 w-5 text-gray-500 shrink-0" />
        <div className="min-w-0">
          <div className="text-xl font-semibold text-gray-900 dark:text-gray-100 truncate">Messagerie</div>
          <div className="text-sm text-gray-500 dark:text-gray-400 truncate">Chat de l'équipe</div>
        </div>
      </div>

      {error && (
        <div className="mb-3 shrink-0 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300">
          <div className="inline-flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            {error}
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              Chargement des messages...
            </div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-2 text-gray-400 dark:text-gray-500">
            <MessageCircle className="h-8 w-8" />
            <div className="text-sm">Aucun message pour l'instant. Soyez le premier à écrire !</div>
          </div>
        ) : (
          <div className="space-y-4">
            {groups.map((group) => (
              <div key={group.day}>
                <div className="flex items-center gap-3 my-3">
                  <div className="flex-1 h-px bg-gray-100 dark:bg-gray-800" />
                  <div className="text-xs text-gray-400 dark:text-gray-500 shrink-0">
                    {formatDay(group.messages[0].created_at)}
                  </div>
                  <div className="flex-1 h-px bg-gray-100 dark:bg-gray-800" />
                </div>

                <div className="space-y-2">
                  {group.messages.map((msg) => {
                    const isMe = msg.user_id === currentUserId;
                    return (
                      <div key={msg.id} className={cx("flex", isMe ? "justify-end" : "justify-start")}>
                        <div className={cx("max-w-[75%] flex flex-col gap-1", isMe ? "items-end" : "items-start")}>
                          {!isMe && (
                            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 px-1">
                              {msg.sender_name}
                            </div>
                          )}
                          <div
                            className={cx(
                              "px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words",
                              isMe
                                ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900 rounded-tr-sm"
                                : "bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100 rounded-tl-sm"
                            )}
                          >
                            {msg.content}
                          </div>
                          <div className="text-[11px] text-gray-400 dark:text-gray-500 px-1">
                            {formatTime(msg.created_at)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
        )}
      </div>

      <div className="mt-3 shrink-0">
        <div className="flex items-end gap-2 rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-3">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Écrire un message… (Entrée pour envoyer)"
            rows={1}
            className="flex-1 resize-none bg-transparent text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 outline-none"
            style={{ maxHeight: "120px", overflowY: "auto" }}
            disabled={sending || loading}
          />
          <button
            type="button"
            onClick={sendMessage}
            disabled={!input.trim() || sending || loading}
            className="shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-xl bg-gray-900 text-white dark:bg-white dark:text-gray-900 hover:bg-black dark:hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
            aria-label="Envoyer"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
        <div className="mt-1 text-xs text-gray-400 dark:text-gray-500 px-1">
          Maj+Entrée pour une nouvelle ligne
        </div>
      </div>
    </div>
  );
}
